<template>
  <div class="row">
    <div v-if="!loading" class="col-9 d-flex align-items-center">
      <label :for="elementId" class="control-label sr-only">Voice</label>
      <select :id="elementId" v-model="voiceId"
              class="form-control" name="voice"
              style="width: 100%;"></select>
    </div>
    <div class="col-3 d-flex align-items-center">
      <button type="button" @click="testVoice()" class="btn btn-outline-dark mr-2" :disabled="loading"
              aria-label="Test Voice">
        <i class="fa fa-headphones"></i>
      </button>
      <button type="button" @click="toggleSettings()" class="btn btn-outline-dark" :disabled="loading"
              aria-label="Voice Settings">
        <i class="fa fa-cog"></i>
      </button>
    </div>
    <div v-if="showSettings" class="col-12 mt-3">
      <div class="card">
        <div class="card-body">
          <div class="row">
            <div class="col-6 mb-2">
              <label :for="`speed-control-${idIndex}`" class="control-label">Speed</label>
              <input
                  :id="`speed-control-${idIndex}`"
                  type="number"
                  class="form-control"
                  v-model="speed"
                  v-on:change="handleVoiceChange"
                  :min="minSpeed"
                  :max="maxSpeed"
                  step="0.1"
              />
            </div>
            <template v-if="provider === 'elevenlabs'">
              <div class="col-6 mb-2">
                <label :for="`tts-model-${idIndex}`" class="control-label">Model</label>
                <select :id="`tts-model-${idIndex}`" class="form-control" v-model="modelId"
                        v-on:change="handleVoiceChange">
                  <option value="eleven_multilingual_v2">eleven_multilingual_v2</option>
                  <option value="eleven_flash_v2_5">eleven_flash_v2_5</option>
                  <option value="eleven_turbo_v2_5">eleven_turbo_v2_5</option>
                  <option value="eleven_turbo_v2">eleven_turbo_v2</option>
                  <option value="eleven_flash_v2">eleven_flash_v2</option>
                  <option value="eleven_english_sts_v2">eleven_english_sts_v2</option>
                  <option value="eleven_multilingual_v1">eleven_multilingual_v1</option>
                  <option value="eleven_multilingual_sts_v2">eleven_multilingual_sts_v2</option>
                  <option value="eleven_monolingual_v1">eleven_monolingual_v1</option>
                </select>
              </div>
              <div class="col-6 mb-2">
                <label :for="`stability-${idIndex}`" class="control-label">Stability</label>
                <input :id="`stability-${idIndex}`" type="number" class="form-control" v-model="stability"
                       v-on:change="handleVoiceChange" min="0.0" max="1.0" step="0.01"/>
              </div>
              <div class="col-6 mb-2">
                <label :for="`similarity-boost-${idIndex}`" class="control-label">Similarity Boost</label>
                <input :id="`similarity-boost-${idIndex}`" type="number" class="form-control" v-model="similarityBoost"
                       v-on:change="handleVoiceChange" min="0.0" max="1.0" step="0.01"/>
              </div>
              <div class="col-6 mb-2">
                <label :for="`style-${idIndex}`" class="control-label">Style</label>
                <input :id="`style-${idIndex}`" type="number" class="form-control" v-model="styleExaggeration"
                       v-on:change="handleVoiceChange" min="0" max="10" step="1"/>
              </div>
              <div class="col-6 mb-2">
                <label :for="`speaker-boost-${idIndex}`">Use Speaker Boost</label>
                <input :id="`speaker-boost-${idIndex}`" type="checkbox" v-model="useSpeakerBoost"
                       v-on:change="handleVoiceChange"/>
              </div>
            </template>
            <template v-if="provider === 'openai'">
              <div class="col-12 mb-2">
                <label :for="`instructions-${idIndex}`" class="control-label">Instructions</label>
                <textarea :id="`instructions-${idIndex}`" class="form-control" v-model="instructions"
                          v-on:change="handleVoiceChange" rows="3"></textarea>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, onMounted, computed} from 'vue'

const props = defineProps({
  id: String,
  type: String,
  idIndex: String,
  speed: Number,
  modelId: String,
  stability: Number,
  similarityBoost: Number,
  styleExaggeration: Number,
  useSpeakerBoost: Boolean,
})

const emit = defineEmits(['handleChange'])
const loading = ref(true)
const showSettings = ref(false)

const voiceId = ref(props.id || null)
const provider = ref('google')
const languageCode = ref('en-US')
const voice = ref('en-US-Wavenet-A')
const speed = ref(props.speed || 1)
const useSpeakerBoost = ref(props.useSpeakerBoost || false)
const modelId = ref(props.modelId || 'eleven_flash_v2_5')
const stability = ref(props.stability || 0.50)
const similarityBoost = ref(props.similarityBoost || 0.75)
const styleExaggeration = ref(props.styleExaggeration || 0)
const instructions = ref('')

const elementId = ref((props.idIndex) ? `voice-select-${props.idIndex}` : 'ai-assistant-select')

const toggleSettings = () => {
  showSettings.value = !showSettings.value
}

const minSpeed = computed(function () {
  return provider.value === 'elevenlabs' ? 0.7 : 0.25
})

const maxSpeed = computed(function() {
  return provider.value === 'elevenlabs' ? 1.2 : 4.0
})

async function fetchVoices() {
  let resp = await fetch(`/api/tts/voices`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'x-api-token': window.apiToken
    }
  })
  resp = await resp.json()
  let voices = resp.map(item => ({
    id: item.voice,
    text: item.label,
    languageCode: item.languageCode,
  }))
  loading.value = false
  return voices
}

async function testVoice() {
  let response = await fetch('/api/tts/generate-audio', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-token': window.apiToken
    },
    body: JSON.stringify({
      text: 'Good morning, how are you today?',
      languageCode: languageCode.value,
      voice: voice.value,
      provider: provider.value,
      speed: speed.value,
      opts: provider.value === 'openai' ? { instructions: instructions.value } : {}
    })
  })

  if (!response.ok) {
    let body = await response.json()
    let error = JSON.stringify(body.errors) || 'Error: ' + body.statusText
    alert(error)
    return
  }

  let audio = await new Response(response.body).blob()

  let file = new File([audio], 'recording.mp3', {type: 'audio/mp3'})
  const audioUrl = URL.createObjectURL(file)

  try {
    const audioElement = new Audio()
    audioElement.src = audioUrl
    audioElement.play().catch((error) => {
      console.error("Failed to play audio: ", error)
    })
  } catch (error) {
    console.error("Failed to play audio: ", error)
  }
}

const handleVoiceChange = () => {
  emit('handleVoiceChange', {
    voiceId: voiceId.value,
    index: props.idIndex,
    elementId: elementId.value,
    speed: speed.value,
    opts: {
      model_id: modelId.value,
      stability: stability.value,
      similarity_boost: similarityBoost.value,
      style_exaggeration: styleExaggeration.value,
      use_speaker_boost: useSpeakerBoost.value,
      instructions: instructions.value
    }
  })
}

onMounted(async () => {
  const voices = await fetchVoices()

  $(() => {
    const $selectEl = $(`#${elementId.value}`)
    const data = _.map(voices, (tag) => {
      const isSelected = tag.id.split('_')[1] === voiceId.value
      if (isSelected) {
        voiceId.value = tag.id
        provider.value = tag.id.split('_')[0]
        voice.value = tag.id.split('_')[1]
        languageCode.value = tag.languageCode || 'en-US'
      }
      return {
        id: tag.id,
        text: tag.text,
        selected: isSelected,
        languageCode: tag.languageCode || 'en-US',
      }
    })
    $selectEl.select2({
      data: data
    }).on('change', function () {
      let selected = $selectEl.select2('data')
      let split = selected[0].id.split('_')
      let langCode = data.find(d => d.id === selected[0].id)
      provider.value = split[0]
      console.log(provider.value)
      voice.value = split[1]
      languageCode.value = langCode?.languageCode || 'en-US'
      console.log(languageCode.value)
      voiceId.value = selected[0].id
      handleVoiceChange()
    })
  })
})
</script>
